// © Microsoft Corporation. All rights reserved.

import { accentFillRest, DesignSystem, isDarkMode, neutralFillToggleHover, neutralForegroundRest, neutralLayerCard } from "@microsoft/fast-components-styles-msft";
import { onboardingImages, reset } from "@msnews/social-utils";

const avatarSize = "80px";
const underageAvatarSize = "50px";
const successTextColor = "#28623F";
const failedTextColor = "#E22C3B";
const reviewTextColor = "orange";

export const OnboardingPageStyles = (config: DesignSystem) => ({
    onboardingPageContainer: {
        ...reset,
        overflow: "hidden",
        "overflow-y": "auto",
        position: "relative",
        display: "flex",
        "flex-direction": "column",
        "align-items": "center",
        "justify-content": "flex-start",
        width: "500px",
        "max-width": "calc(100vw - 40px)",
        padding: "40px 20px",
        background: neutralLayerCard,
        "text-align": "center",
        "box-shadow": "0px 1.2px 3.6px rgba(0, 0, 0, 0.1), 0px 6.4px 14.4px rgba(0, 0, 0, 0.13)",

        "&:before": {
            content: "' '",
            position: "absolute",
            top: "0",
            height: "8px",
            width: "100%",
            background: accentFillRest
        },

        "& .cwt-top-logo": {
            height: "24px",
            margin: "0 0 40px"
        },

        "& .cwt-ellipse-steps": {
            "text-align": "center",
            "margin-top": "-20px",

            "& .cwt-active-step": {
                height: "8px",
                width: "8px",
                margin: "0 4px",
                display: "inline-block",
                "border-radius": "50%",
                "background-color": "#929292"
            },
            "& .cwt-step": {
                height: "8px",
                width: "8px",
                margin: "0 4px",
                display: "inline-block",
                "border-radius": "50%",
                "background-color": "#EFEFEF"
            }
        },

        "& .cwt-title": {
            margin: "20px 0 0",
            "font-size": "24px",
            padding: "0 16px",
            color: neutralForegroundRest
        },

        "& .cwt-sub-title": {
            "max-width": "400px",
            margin: "16px 0 0",
            "font-size": "13px",
            color: isDarkMode(config) ? "#D9D9D9" : neutralFillToggleHover
        },

        "& .cwt-avatar": {
            "touch-action": "none",
            "margin-top": "36px",
            height: avatarSize,
            width: avatarSize,
            "min-width": avatarSize,

            "&.cwt-error": {
                border: "1px solid "+failedTextColor
            }
        },

        "& .cwt-name": {
            "margin-top": "24px",
            "font-size": "24px",
            color: neutralForegroundRest,

            "&.cwt-error": {
                color: failedTextColor
            }
        },

        "& .cwt-email": {
            "margin-top": "30px",
            "font-size": "18px",
            color: neutralForegroundRest
        },

        "& .cwt-h-seperator": {
            width: "100%",
            "max-width": "380px",
            margin: "24px 0 12px",
            "border-top": "1px solid #949494"
        },

        "& .cwt-status-message": {
            "margin-top": "8px",
            "max-width": "345px",
            "min-height": "36px",
            "font-size": "13px",

            "& .cwt-icon-vector": {
                "font-size": "16px"
            },

            "& .cwt-success-message": {
                color: isDarkMode(config) ? "#BAE3C8" : successTextColor
            },

            "& .cwt-review-message": {
                display: "block",
                "margin-top": "6px",
                color: reviewTextColor
            },

            "& .cwt-error-message": {
                display: "block",
                "margin-top": "6px",

                "& *": {
                    color: failedTextColor
                }
            }
        },

        "& .cwt-buttons": {
            "margin-top": "30px",
            display: "flex",
            padding: "7px 0",

            "& .cwt-fast-button": {
                margin: "0 4px"
            }
        },

        "& .cwt-footer-text": {
            "margin-top": "14px",
            "max-width": "345px",
            padding: "0 16px",
            "font-size": "13px",
            color: isDarkMode(config) ? "#D9D9D9" : neutralFillToggleHover,

            "& .cwt-link": {
                color: isDarkMode(config) ? "#BDDCFF" : accentFillRest
            }
        }
    },

    onboardingUnderagePageContainer: {
        ...reset,
        overflow: "hidden",
        position: "relative",
        display: "flex",
        "flex-direction": "column",
        "align-items": "center",
        "justify-content": "flex-start",
        width: "540px",
        "max-width": "100%",
        height: "470px",
        "max-height": "90vh",
        padding: "40px 0 54px",
        background: `center bottom no-repeat url('${onboardingImages.underageBg}')`,
        "background-color": neutralLayerCard,
        "text-align": "center",
        "box-shadow": "0px 1.2px 3.6px rgba(0, 0, 0, 0.1), 0px 6.4px 14.4px rgba(0, 0, 0, 0.13)",

        "&:before": {
            content: "' '",
            position: "absolute",
            top: "0",
            height: "8px",
            width: "100%",
            background: accentFillRest
        },

        "& .cwt-title": {
            "font-size": "24px",
            "max-width": "380px",
            padding: "0 16px",
            color: neutralForegroundRest
        },

        "& .cwt-sub-title": {
            "max-width": "400px",
            margin: "20px 16px 0",
            "font-size": "13px",
            color: isDarkMode(config) ? "#D9D9D9" : neutralFillToggleHover
        },

        "& .cwt-avatar": {
            "touch-action": "none",
            margin: "16px 0 36px",
            height: underageAvatarSize,
            width: underageAvatarSize,
            "min-width": underageAvatarSize
        },

        "& .cwt-buttons": {
            "margin-top": "40px",
            display: "flex",
            padding: "7px 0",

            "& .cwt-fast-button": {
                margin: "0 4px"
            }
        }
    },
    onboardingWelcomePageStep2Container: {
        ...reset,
        overflow: "hidden",
        position: "relative",
        display: "flex",
        "flex-direction": "column",
        "align-items": "center",
        "justify-content": "flex-start",
        width: "540px",
        "max-width": "100%",
        "max-height": "60vh",
        height: "470px",
        padding: "40px 0 54px",
        background: `center bottom -130px no-repeat url('${onboardingImages.welcomeBg}')`,
        "background-color": neutralLayerCard,
        "text-align": "center",
        "box-shadow": "0px 1.2px 3.6px rgba(0, 0, 0, 0.1), 0px 6.4px 14.4px rgba(0, 0, 0, 0.13)",

        "&:before": {
            content: "' '",
            position: "absolute",
            top: "0",
            height: "8px",
            width: "100%",
            background: accentFillRest
        },

        "& .cwt-ellipse-steps": {
            "text-align": "center",
            "margin-top": "-20px",

            "& .cwt-active-step": {
                height: "8px",
                width: "8px",
                margin: "8px 4px",
                display: "inline-block",
                "border-radius": "50%",
                "background-color": "#929292"
            },
            "& .cwt-step": {
                height: "8px",
                width: "8px",
                margin: "8px 4px",
                display: "inline-block",
                "border-radius": "50%",
                "background-color": "#EFEFEF"
            }
        },

        "& .cwt-title": {
            "font-size": "24px",
            "margin-top": "20px",
            width: "381px",
            height: "61px",
            padding: "0 16px",
            color: neutralForegroundRest
        },

        "& .cwt-sub-title": {
            "max-width": "400px",
            margin: "-10px 16px 0",
            "font-size": "13px",
            color: isDarkMode(config) ? "#D9D9D9" : neutralFillToggleHover,

            "&.cwt-welcome": {
                margin: "5px 16px 0"
            }
        },

        "& .cwt-avatar-step2": {
            "touch-action": "none",
            "margin-top": "12px",
            height: underageAvatarSize,
            width: underageAvatarSize,
            "border-radius": "50%"
        },

        "& .cwt-buttons": {
            "margin-top": "21px",
            display: "flex",
            padding: "7px 0",

            "& .cwt-fast-button": {
                margin: "0 4px"
            },
            "& .cwt-button-ok": {
                width: "136px",
                height: "40px"
            }
        },

        "& .cwt-image-container": {
            "margin-top": "-66px",
            "margin-bottom": "-60px",

            "& .cwt-image": {
                width: "350px",
                "object-fit": "center",
                "object-position": "0 130px"
            }
        }
    }
});